import { get } from "lockr";
import { User } from "../types/common.types";

export const permissionAliases: Record<string, string[]> = {
  order_access: [
    "order_view",
    "order_update_status",
    "table_session_order_create",
    "table_session_payment_record",
  ],
  product_write: ["product_view", "product_write"],
  settings_modification: [
    "business_settings_view",
    "business_settings_write",
    "menu_settings_view",
    "menu_settings_write",
  ],
  can_generate_barcode: ["dashboard_view", "segment_map_view"],
};

export const getCurrentStaffPermissions = (): string[] | undefined => {
  return get("staff-permissions") as string[] | undefined;
};

export const expandPermissions = (permissions: string[] = []) => {
  return Array.from(
    new Set(
      permissions.flatMap((permission) => permissionAliases[permission] || permission)
    )
  );
};

export const hasStaffSession = () => {
  return Array.isArray(getCurrentStaffPermissions());
};

export const can = (permission: string) => {
  if (permission === "account_settings") return true;
  const permissions = getCurrentStaffPermissions();
  if (!permissions) return true;
  return expandPermissions(permissions).includes(permission);
};

export const getCurrentFeatures = (): string[] => {
  const user = get("user") as User | undefined;
  return user?.features || [];
};

export const hasFeature = (featureKey: string) => {
  const features = getCurrentFeatures();
  return features.includes(featureKey);
};

export const hasAnyFeature = (features: string[] = []) => {
  if (!features.length) return true;
  const current = getCurrentFeatures();
  return features.some((feature) => current.includes(feature));
};

export const canAny = (permissions: string[] = []) => {
  if (!permissions.length) return true;
  const staffPermissions = getCurrentStaffPermissions();
  if (!staffPermissions) return true;
  const expanded = expandPermissions(staffPermissions);
  return permissions.some((permission) => expanded.includes(permission));
};

export const canAll = (permissions: string[] = []) => {
  if (!permissions.length) return true;
  const staffPermissions = getCurrentStaffPermissions();
  if (!staffPermissions) return true;
  const expanded = expandPermissions(staffPermissions);
  return permissions.every((permission) => expanded.includes(permission));
};

export const getDefaultStaffLandingRoute = () => {
  if (can("dashboard_view")) return "/app/dashboard";
  if (can("segment_map_view")) return "/app/dashboard";
  if (can("order_view")) return "/app/order";
  if (can("product_view")) return "/app/drinks";
  if (can("staff_view")) return "/app/staff-management";
  if (
    canAny([
      "business_settings_view",
      "menu_settings_view",
      "billing_view",
      "payment_provider_view",
    ])
  ) {
    return "/app/settings";
  }
  return "/app/dashboard";
};
